/**
 * PasteConfirmDialog — polished confirmation before pasting an optimizer recipe
 * onto the selected flows. Shows source → targets, what changes, what's kept.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useState, type FC } from 'react';
import { ArrowRight, ClipboardCheck, ShieldCheck } from 'lucide-react';
import { trackerLabel } from '@trafficpulse/shared';

import { Button, LoadingSpinner } from '@/components/ui';

import {
  defaultPasteFields,
  type CopiedOptimizerConfig,
  type PasteFieldKey,
} from '../optimizer-clipboard.types';
import { PasteFieldsChecklist } from './PasteFieldsChecklist';
import { backdropClick } from '@/components/ui';

export interface PasteConfirmDialogProps {
  copied: CopiedOptimizerConfig;
  targetCount: number;
  isBusy: boolean;
  onConfirm: (fields: Set<PasteFieldKey>) => void;
  onCancel: () => void;
}

export const PasteConfirmDialog: FC<PasteConfirmDialogProps> = ({
  copied,
  targetCount,
  isBusy,
  onConfirm,
  onCancel,
}) => {
  const { source, recipe } = copied;
  const provider = trackerLabel(source.provider);
  const flowWord = targetCount === 1 ? 'flow' : 'flows';
  const groups = recipe.formula.groups.length;
  const [fields, setFields] = useState<Set<PasteFieldKey>>(() =>
    defaultPasteFields(recipe),
  );
  const toggle = (key: PasteFieldKey) =>
    setFields((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm"
      onClick={backdropClick(!isBusy ? onCancel : undefined)}
      role="dialog"
      aria-modal="true"
      aria-label="Paste optimizer config"
    >
      <div
        className="relative w-full max-w-lg overflow-hidden rounded-2xl border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 border-b border-border px-5 py-4">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <ClipboardCheck className="h-5 w-5" aria-hidden />
          </span>
          <div className="min-w-0">
            <h3 className="text-base font-semibold leading-tight">Paste optimizer config</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Apply the copied recipe to the selected flows.
            </p>
          </div>
        </div>

        <div className="flex items-stretch gap-3 px-5 py-4">
          <div className="min-w-0 flex-1 rounded-lg border border-border bg-muted/30 px-3 py-2">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Source
            </p>
            <p className="truncate text-sm font-medium" title={source.campaignName}>
              {source.campaignName}
            </p>
            <p className="text-[11px] text-muted-foreground">
              {provider} · {recipe.mode} · {groups} formula group{groups === 1 ? '' : 's'}
            </p>
          </div>
          <ArrowRight className="h-4 w-4 shrink-0 self-center text-muted-foreground" aria-hidden />
          <div className="min-w-0 flex-1 rounded-lg border border-primary/40 bg-primary/5 px-3 py-2">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Targets
            </p>
            <p className="text-sm font-medium tabular-nums">
              {targetCount} {flowWord}
            </p>
            <p className="text-[11px] text-muted-foreground">selected in the list</p>
          </div>
        </div>

        <div className="px-5">
          <PasteFieldsChecklist
            selected={fields}
            onToggle={toggle}
            hasPackage={Boolean(recipe.packageId)}
          />
        </div>

        <div className="mx-5 my-4 flex items-start gap-2 rounded-lg border border-success/30 bg-success/5 px-3 py-2.5 text-xs text-muted-foreground">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden />
          <span>
            <span className="font-medium text-foreground">Left untouched:</span> auto-run &amp; pause
            state and each flow&apos;s live snapshot. No weights are pushed live.{' '}
            <span className="font-medium text-foreground">Not copied:</span> per-path
            skip lists — they name paths of the flow they were set on, so they
            cannot mean anything on another campaign.
          </span>
        </div>

        <div className="flex justify-end gap-2 border-t border-border bg-muted/10 px-5 py-3">
          <Button variant="outline" onClick={onCancel} disabled={isBusy}>
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(fields)}
            disabled={isBusy || fields.size === 0}
            aria-busy={isBusy}
            className="gap-2"
          >
            {isBusy && <LoadingSpinner size="sm" />}
            Paste to {targetCount} {flowWord}
          </Button>
        </div>
      </div>
    </div>
  );
};
